import React, { useEffect } from "react";
import logo from "assets/img/logo.png";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import config from "config";
import packageJson from "../../../package.json";

import {
  Box,
  Flex,
  Heading,
  Text,
  Link,
  keyframes,
  VStack,
  HStack,
  Container,
  UnorderedList,
  ListItem,
  OrderedList,
  useBreakpointValue,
  Icon,
} from "@chakra-ui/react";
import MufynButton from "components/Button";
import { FaArrowLeft, FaEnvelope, FaTrashAlt, FaShieldAlt, FaClock } from "react-icons/fa";

// ============================================
// ANIMATIONS
// ============================================

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const floatReverse = keyframes`
  0%, 100% { transform: translateY(-10px) rotate(-2deg); }
  50% { transform: translateY(10px) rotate(2deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const slideUp = keyframes`
  0% { opacity: 0; transform: translateY(40px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================
// ACCOUNT DELETION PAGE
// ============================================

function AccountDeletionPage() {
  const { t, i18n } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? true;

  // Browser language detection
  useEffect(() => {
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      return ['en', 'fr'].includes(langCode) ? langCode : 'en';
    };
    const browserLang = getBrowserLanguage();
    if (browserLang !== 'en') i18n.changeLanguage(browserLang);
  }, [i18n]);

  useEffect(() => {
    document.body.style.overflow = "auto";
    return () => { document.body.style.overflow = "unset"; };
  }, []);

  // Helper for section headings
  const SectionTitle = ({ icon, children }) => (
    <Flex align="center" gap={3}
      mt={10} mb={3} pb={2}
      borderBottom="1px solid rgba(255, 255, 255, 0.06)"
    >
      {icon && <Icon as={icon} color="#4ade80" boxSize={5} />}
      <Heading
        fontSize={{ base: "lg", md: "xl" }}
        fontWeight="bold"
        color="white"
      >
        {children}
      </Heading>
    </Flex>
  );

  // Helper for paragraphs
  const P = ({ children, ...props }) => (
    <Text color="gray.400" fontSize={{ base: "sm", md: "md" }} lineHeight="1.8" mt={2} {...props}>
      {children}
    </Text>
  );

  // Helper for lists
  const BulletList = ({ children }) => (
    <UnorderedList spacing={1} pl={4} mt={2} color="gray.400" fontSize={{ base: "sm", md: "md" }} lineHeight="1.8">
      {children}
    </UnorderedList>
  );

  return (
    <Box minH="100vh" w="100%" position="relative" bg="#0a0d14" overflowX="hidden">
      {/* ANIMATED BACKGROUND */}
      <Box position="fixed" top="0" left="0" right="0" bottom="0" zIndex="0" pointerEvents="none">
        <Box
          position="absolute" top="0" left="0" right="0" bottom="0"
          bgGradient="radial(ellipse at 30% 20%, rgba(34, 197, 94, 0.12) 0%, transparent 50%), radial(ellipse at 70% 80%, rgba(16, 185, 129, 0.08) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)"
        />
        <Box
          position="absolute" top="10%" left="5%"
          w={{ base: "200px", md: "350px" }} h={{ base: "200px", md: "350px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)"
          filter="blur(50px)" animation={`${float} 10s ease-in-out infinite`}
        />
        <Box
          position="absolute" bottom="20%" right="5%"
          w={{ base: "250px", md: "450px" }} h={{ base: "250px", md: "450px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(74, 222, 128, 0.12) 0%, transparent 70%)"
          filter="blur(70px)" animation={`${floatReverse} 12s ease-in-out infinite`}
        />
        <Box
          position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
          w={{ base: "400px", md: "700px" }} h={{ base: "400px", md: "700px" }}
          borderRadius="full" bg="radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 60%)"
          filter="blur(100px)" animation={`${pulse} 8s ease-in-out infinite`}
        />
        <Box
          position="absolute" top="0" left="0" right="0" bottom="0" opacity="0.03"
          backgroundImage={`linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`}
          backgroundSize="80px 80px"
        />
        <Box
          position="absolute" top="25%" left="0" right="0" h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.2), transparent)"
          animation={`${shimmer} 4s linear infinite`} backgroundSize="200% 100%"
        />
      </Box>

      {/* HEADER */}
      <Box position="relative" zIndex="1" pt={{ base: 6, md: 8 }} pb={{ base: 4, md: 6 }}>
        <Container maxW="container.md" px={{ base: 4, md: 6 }}>
          <Flex justify="space-between" align="center">
            <MufynButton
              as={RouterLink}
              to="/auth/landing"
              variant="ghost"
              size="sm"
              leftIcon={<FaArrowLeft />}
            >
              {t("account_deletion.back")}
            </MufynButton>
            <HStack spacing={3}>
              <img src={logo} width="32px" alt="Mufyn" />
              <Text color="white" fontWeight="bold" fontSize="lg">{config.appName}</Text>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* CONTENT */}
      <Box position="relative" zIndex="1" pb={{ base: 12, md: 20 }}>
        <Container maxW="container.md" px={{ base: 4, md: 6 }}>

          {/* Title */}
          <VStack spacing={3} textAlign="center" mb={10} animation={`${slideUp} 0.8s ease-out`}>
            <Heading
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="black"
              bgGradient="linear(to-r, white, gray.300)"
              bgClip="text"
            >
              {t("account_deletion.title")}
            </Heading>
            <Text color="gray.400" fontSize={{ base: "sm", md: "md" }} maxW="600px">
              {t("account_deletion.subtitle")}
            </Text>
          </VStack>

          {/* App & Developer Info */}
          <Box
            bg="rgba(255, 255, 255, 0.03)"
            border="1px solid rgba(255, 255, 255, 0.06)"
            borderRadius="xl"
            p={{ base: 5, md: 7 }}
            mb={8}
            animation={`${slideUp} 1s ease-out`}
          >
            <HStack spacing={4} mb={3}>
              <img src={logo} width="48px" alt="Mufyn" />
              <Box>
                <Text color="white" fontWeight="bold" fontSize="lg">{config.appName}</Text>
                <Text color="gray.400" fontSize="sm">{t("account_deletion.developer_label")}</Text>
              </Box>
            </HStack>
            <P>{t("account_deletion.app_description")}</P>
          </Box>

          {/* 1. How to request deletion */}
          <SectionTitle icon={FaEnvelope}>{t("account_deletion.sections.procedure.title")}</SectionTitle>
          <P>{t("account_deletion.sections.procedure.intro")}</P>
          <OrderedList spacing={3} pl={4} mt={4} color="gray.400" fontSize={{ base: "sm", md: "md" }} lineHeight="1.8">
            <ListItem>{t("account_deletion.sections.procedure.step1")}</ListItem>
            <ListItem>{t("account_deletion.sections.procedure.step2")}</ListItem>
            <ListItem>{t("account_deletion.sections.procedure.step3")}</ListItem>
            <ListItem>{t("account_deletion.sections.procedure.step4")}</ListItem>
          </OrderedList>

          {/* Email CTA */}
          <Box
            mt={6}
            bg="rgba(74, 222, 128, 0.06)"
            border="1px solid rgba(74, 222, 128, 0.15)"
            borderRadius="xl"
            p={{ base: 5, md: 7 }}
            textAlign="center"
          >
            <Icon as={FaEnvelope} color="#4ade80" boxSize={8} mb={3} />
            <Text color="white" fontWeight="bold" fontSize={{ base: "md", md: "lg" }} mb={2}>
              {t("account_deletion.sections.procedure.cta_title")}
            </Text>
            <Text color="gray.400" fontSize={{ base: "sm", md: "md" }} mb={4}>
              {t("account_deletion.sections.procedure.cta_description")}
            </Text>
            <Link
              href={`mailto:contact@nelx.net?subject=${encodeURIComponent(t("account_deletion.sections.procedure.email_subject"))}`}
              color="#4ade80"
              fontWeight="bold"
              fontSize={{ base: "lg", md: "xl" }}
              _hover={{ color: "#22c55e", textDecoration: "underline" }}
              transition="color 0.2s"
            >
              contact@nelx.net
            </Link>
          </Box>

          {/* 2. Data deleted */}
          <SectionTitle icon={FaTrashAlt}>{t("account_deletion.sections.data_deleted.title")}</SectionTitle>
          <P>{t("account_deletion.sections.data_deleted.intro")}</P>
          <BulletList>
            <ListItem>{t("account_deletion.sections.data_deleted.item1")}</ListItem>
            <ListItem>{t("account_deletion.sections.data_deleted.item2")}</ListItem>
            <ListItem>{t("account_deletion.sections.data_deleted.item3")}</ListItem>
            <ListItem>{t("account_deletion.sections.data_deleted.item4")}</ListItem>
            <ListItem>{t("account_deletion.sections.data_deleted.item5")}</ListItem>
            <ListItem>{t("account_deletion.sections.data_deleted.item6")}</ListItem>
            <ListItem>{t("account_deletion.sections.data_deleted.item7")}</ListItem>
            <ListItem>{t("account_deletion.sections.data_deleted.item8")}</ListItem>
          </BulletList>
          <P fontWeight="semibold" color="#4ade80">{t("account_deletion.sections.data_deleted.conclusion")}</P>

          {/* 3. Data retained */}
          <SectionTitle icon={FaShieldAlt}>{t("account_deletion.sections.data_retained.title")}</SectionTitle>
          <P>{t("account_deletion.sections.data_retained.intro")}</P>
          <P>{t("account_deletion.sections.data_retained.details")}</P>

          {/* 4. Processing time */}
          <SectionTitle icon={FaClock}>{t("account_deletion.sections.processing.title")}</SectionTitle>
          <P>{t("account_deletion.sections.processing.p1")}</P>
          <P>{t("account_deletion.sections.processing.p2")}</P>

          {/* Contact */}
          <Box mt={14} pt={8} borderTop="1px solid rgba(255, 255, 255, 0.06)" textAlign="center">
            <Heading fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="white" mb={3}>
              {t("account_deletion.contact.title")}
            </Heading>
            <Text fontSize={{ base: "sm", md: "md" }} color="gray.400" mb={4}>
              {t("account_deletion.contact.description")}
            </Text>
            <Link
              href="mailto:contact@nelx.net"
              color="#4ade80"
              fontWeight="semibold"
              fontSize={{ base: "md", md: "lg" }}
              _hover={{ color: "#22c55e", textDecoration: "underline" }}
              transition="color 0.2s"
            >
              contact@nelx.net
            </Link>
          </Box>

        </Container>
      </Box>

      {/* FOOTER */}
      <Box py={{ base: 8, md: 10 }} borderTop="1px solid rgba(255, 255, 255, 0.05)" position="relative" zIndex="1">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Flex direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={4}>
            <HStack spacing={3}>
              <img src={logo} width="32px" alt="Mufyn" />
              <Text color="gray.500" fontSize="sm">
                © {new Date().getFullYear()} {config.appName}. {t("landing.footer.rights")}
              </Text>
            </HStack>
            <HStack spacing={6}>
              <Text color="gray.600" fontSize="xs" fontFamily="monospace">
                v{packageJson.version}
              </Text>
              <Link href="https://nelx.net" isExternal color="gray.500" fontSize="sm" _hover={{ color: "#4ade80" }} transition="color 0.2s">
                {t("landing.footer.by")} NELX
              </Link>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}

export default AccountDeletionPage;
